/**
 * THE SEAM.
 *
 * Everything above this file talks to `DashboardApi`, never to fetch() and
 * never to a mock. Swapping VITE_API_MODE from "mock" to "http" is the whole
 * of "wiring up the backend" — if it ever turns into more than that, the seam
 * has been violated somewhere and that is a bug worth fixing immediately.
 */
import type {
  CompanyProfile,
  DashboardSummary,
  DecisionDocument,
  Executive,
  Page,
  RecommendedExecutivesQuery,
  KpiTile,
  MarketInsight,
  NextStep,
  ActiveEngagement,
  MatchPreview,
  RolePipeline,
  SavedSearch,
  TeamActivityEvent,
  TeamMember,
  TrustProfile,
} from './contract'
import { mockApi } from './mock'

export interface DashboardApi {
  getRecommendedExecutives(q?: RecommendedExecutivesQuery): Promise<Page<Executive>>
  getDecisionDocuments(): Promise<Page<DecisionDocument>>
  getTeamActivity(): Promise<Page<TeamActivityEvent>>
  getTrustProfile(): Promise<TrustProfile>
  getCompanyProfile(): Promise<CompanyProfile>
  getDashboardSummary(): Promise<DashboardSummary>
  getKpis(): Promise<Page<KpiTile>>
  getMarketInsights(): Promise<Page<MarketInsight>>
  getNextStep(): Promise<NextStep>
  getSavedSearches(): Promise<Page<SavedSearch>>
  getRolePipelines(): Promise<Page<RolePipeline>>
  getTopMatches(): Promise<Page<MatchPreview>>
  /** Section 9 — recently viewed / shortlisted / shared, per tab. */
  getRecentExecutives(tab?: 'viewed' | 'shortlisted' | 'shared'): Promise<Page<Executive>>
  getActiveEngagements(): Promise<Page<ActiveEngagement>>
  getTeamMembers(): Promise<Page<TeamMember>>
}

/** Real implementation. Unused until the FastAPI service exists. */
export function createHttpApi(baseUrl: string): DashboardApi {
  async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    const url = new URL(`${baseUrl}${path}`, window.location.origin)
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v))
    }

    let res: Response
    try {
      res = await fetch(url, { credentials: 'include' })
    } catch (cause) {
      throw { status: 0, code: 'network', message: 'Could not reach the server', cause }
    }

    if (!res.ok) {
      throw {
        status: res.status,
        code:
          res.status === 401 ? 'unauthorized'
          : res.status === 403 ? 'forbidden'
          : res.status === 404 ? 'not_found'
          : res.status === 429 ? 'rate_limited'
          : 'server_error',
        message: await res.text().catch(() => res.statusText),
      }
    }
    return res.json() as Promise<T>
  }

  return {
    getRecommendedExecutives: (q) => get('/executives/recommended', q as Record<string, unknown>),
    getDecisionDocuments: () => get('/decision-documents'),
    getTeamActivity: () => get('/team-activity'),
    getTrustProfile: () => get('/company/trust-profile'),
    getCompanyProfile: () => get('/company/profile'),
    getDashboardSummary: () => get('/dashboard/summary'),
    getKpis: () => get('/dashboard/kpis'),
    getMarketInsights: () => get('/market-insights'),
    getNextStep: () => get('/dashboard/next-step'),
    getSavedSearches: () => get('/saved-searches'),
    getRolePipelines: () => get('/role-pipelines'),
    getTopMatches: () => get('/executives/top-matches'),
    getRecentExecutives: (tab) => get('/executives/recent', { tab }),
    getActiveEngagements: () => get('/engagements/active'),
    getTeamMembers: () => get('/team/members'),
  }
}

const mode = import.meta.env.VITE_API_MODE ?? 'mock'

export const api: DashboardApi =
  mode === 'http' ? createHttpApi(import.meta.env.VITE_API_BASE_URL ?? '/api') : mockApi
